import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, X } from "lucide-react";

/**
 * BillingProfileModal — modal global déclenché par l'intercepteur axios sur
 * 422 BILLING_PROFILE_INCOMPLETE (nom de compagnie et/ou SIRET manquant,
 * requis pour créer une facture ou l'envoyer en e-facturation).
 * Monté une seule fois dans App.js, même pattern que UpgradeModal.
 * Écoute l'événement window « billing_profile_incomplete ».
 */
export default function BillingProfileModal() {
  const navigate = useNavigate();
  const [state, setState] = useState(null); // null = fermé | { missing_fields, message }

  const handleEvent = useCallback((e) => {
    setState(e.detail);
  }, []);

  useEffect(() => {
    window.addEventListener("billing_profile_incomplete", handleEvent);
    return () => window.removeEventListener("billing_profile_incomplete", handleEvent);
  }, [handleEvent]);

  const close = useCallback(() => setState(null), []);

  const goProfile = useCallback(() => {
    setState(null);
    navigate("/profile?tab=profile");
  }, [navigate]);

  if (!state) return null;

  const missing = state.missing_fields || [];

  return (
    <div style={s.backdrop} onClick={close}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <button style={s.closeBtn} onClick={close} aria-label="Fermer">
          <X size={18} />
        </button>

        <div style={s.iconCircle}>
          <Building2 size={28} />
        </div>

        <h2 style={s.title}>Profil de facturation incomplet</h2>

        <p style={s.sub}>{state.message}</p>

        {missing.length > 0 && (
          <div style={s.missingList}>
            {missing.map((f, i) => (
              <div key={i} style={s.missingRow}>
                <span style={s.dot}>&#8226;</span>
                <span style={s.missingText}>{f}</span>
              </div>
            ))}
          </div>
        )}

        <button onClick={goProfile} style={s.btn}>
          Compléter mon profil →
        </button>

        <button onClick={close} style={s.skipBtn}>Plus tard</button>
      </div>
    </div>
  );
}

const s = {
  backdrop: {
    position: "fixed", inset: 0, zIndex: 9999,
    background: "rgba(0,0,0,0.55)", backdropFilter: "blur(3px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "16px",
  },
  modal: {
    background: "#fff", borderRadius: "20px", padding: "40px 36px",
    maxWidth: "420px", width: "100%",
    display: "flex", flexDirection: "column", alignItems: "center", gap: "16px",
    textAlign: "center", fontFamily: "'DM Sans', sans-serif",
    boxShadow: "0 24px 80px rgba(0,0,0,0.2)",
    position: "relative",
  },
  closeBtn: {
    position: "absolute", top: "16px", right: "16px",
    background: "none", border: "none", cursor: "pointer",
    color: "#9aaa9a", display: "flex", alignItems: "center", padding: "4px",
  },
  iconCircle: {
    width: "64px", height: "64px", borderRadius: "20px",
    display: "flex", alignItems: "center", justifyContent: "center",
    background: "#158d3818", color: "#158d38",
  },
  title: { fontSize: "18px", fontWeight: "800", color: "#181d27", lineHeight: 1.4, margin: 0 },
  sub: { fontSize: "14px", color: "#6b7b6b", lineHeight: 1.6, margin: 0 },
  missingList: {
    width: "100%", display: "flex", flexDirection: "column", gap: "8px",
    background: "#f8faf8", borderRadius: "12px", padding: "16px 20px", textAlign: "left",
  },
  missingRow: { display: "flex", gap: "10px", alignItems: "flex-start" },
  dot: { fontSize: "13px", fontWeight: "800", flexShrink: 0, color: "#158d38" },
  missingText: { fontSize: "13px", color: "#374737", lineHeight: 1.4 },
  btn: {
    width: "100%", padding: "14px", borderRadius: "12px", border: "none",
    background: "#158d38", color: "white", fontSize: "15px", fontWeight: "700", cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif",
  },
  skipBtn: {
    background: "none", border: "none", color: "#9aaa9a",
    fontSize: "13px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
  },
};
